import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import CustomPropTypes from 'CustomPropTypes';
import SelfServePostBuilderSidePanel from 'SelfServePostBuilderSidePanel';
import getSuperScriptValue from 'SelfServeBuilderUtils';
import SelfServeBuilderPDFPageCanvas from './SelfServeBuilderPDFPageCanvas';
import SelfServeBuilderToolbar from './SelfServeBuilderToolbar';

const BUILDER_EDITOR_Z_INDEX = 1;
const BUILDER_EDITOR_WIDTH = 1275;
const BUILDER_EDITOR_HEIGHT = 1650;
const DOCUMENT_PREVIEW_PADDING = 10;
const RESIZE_DELTA = 500;

class SelfServeBuilderCanvas extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      height: window.innerHeight,
      width: window.innerWidth,
      forceUpdate: false,
      isMounted: false,
    };
    this.getPopupZoomFactor = this.getPopupZoomFactor.bind(this);

    this.resizeTimeout = false;
    this.onResizeEnd = this.onResizeEnd.bind(this);
    this.initializeResizeListener = this.initializeResizeListener.bind(this);
    this.getSuperScriptValue = this.getSuperScriptValue.bind(this);
    this.getPDFScale = this.getPDFScale.bind(this);
    this.getPDFScaleWithScalingEnabled = this.getPDFScaleWithScalingEnabled.bind(this);
    this.reRender = this.reRender.bind(this);
    this.isTemplateEmpty = this.isTemplateEmpty.bind(this);
  }

  componentDidMount() {
    if (this.props.isPreviewMode) {
      this.initializeResizeListener();
    } else if (!this.props.isPreviewMode && this.props.isTourDisplayed) {
      this.initializeResizeListener();
    }
  }

  componentWillUnmount() {
    if (this.props.isPreviewMode || (!this.props.isPreviewMode && this.props.isTourDisplayed)) {
      window.removeEventListener('resize', this.onResizeEnd);
    }
  }

  // Used with updateDimensions to resize canvas after resize event is done
  onResizeEnd() {
    if (this.props.isPreviewMode) {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(this.reRender, RESIZE_DELTA);
    }
  }

  getSuperScriptValue(id, label, isGroup = false) {
    const superscriptValue = getSuperScriptValue(
      id,
      label,
      isGroup ? this.props.popupGroups : this.props.popups,
    );
    return superscriptValue > 0 ? <sup>{superscriptValue}</sup> : null;
  }

  getPDFScale() {
    if (!this.props.pages || this.props.pages.length <= 0) return 1;
    const initialViewport = this.props.pages[0].page.getViewport(1);
    const defaultScale = BUILDER_EDITOR_HEIGHT / initialViewport.height;
    if (this.props.isScalingEnabled) {
      const pageHeight = initialViewport.height;
      return this.getPDFScaleWithScalingEnabled(defaultScale, pageHeight);
    }
    return defaultScale;
  }

  getPDFScaleWithScalingEnabled(defaultScale, pageHeight) {
    if (!this.props.editorRef.current) return defaultScale;
    const containerWidth = this.props.editorRef.current.clientWidth - DOCUMENT_PREVIEW_PADDING;

    const freewriteTrueHeightScale = BUILDER_EDITOR_HEIGHT / pageHeight;
    const freewriteTrueWidthScale = BUILDER_EDITOR_WIDTH / containerWidth;
    return freewriteTrueHeightScale / freewriteTrueWidthScale;
  }

  getPopupZoomFactor() {
    const { pages, isPreviewMode, editorRef } = this.props;
    if (!pages || pages.length <= 0 || !isPreviewMode) return 1;
    let zoomFactor = 1;
    let width = window.innerWidth;
    const recipientPreviewContainer = editorRef.current;
    if (recipientPreviewContainer) {
      width = recipientPreviewContainer.clientWidth - DOCUMENT_PREVIEW_PADDING;
    }
    zoomFactor = width / BUILDER_EDITOR_WIDTH;
    return zoomFactor;
  }

  isTemplateEmpty() {
    return Object.keys(this.props.popups).length === 0;
  }

  // Allows editor to resize according to window
  initializeResizeListener() {
    window.addEventListener('resize', this.onResizeEnd);
  }

  reRender() {
    this.setState(state => ({
      forceUpdate: !state.forceUpdate,
    }));
  }

  render() {
    const {
      isPreviewMode,
      pages,
      popupIdsOnPage,
      popupGroupsOnPage,
      popups,
      popupGroups,
    } = this.props;
    const scale = this.getPDFScale();
    const zoomFactor = this.getPopupZoomFactor();
    return (
      <React.Fragment>
        {!isPreviewMode && (
          <SelfServeBuilderToolbar
            isPreviewAutoOpenDisabled={this.props.isPreviewAutoOpenDisabled}
            isPreviewMode={isPreviewMode}
          />
        )}
        {!isPreviewMode && !this.props.isLoading && (
          <SelfServePostBuilderSidePanel
            isOverlayVisible={this.props.isOverlayVisible}
            isTemplateEmpty={this.isTemplateEmpty()}
            openWidth={352}
          />
        )}
        <div
          id="selfServeBuilderEditorWrapper"
          className="self-serve-builder-editor-wrapper"
          style={{
            zIndex: BUILDER_EDITOR_Z_INDEX,
          }}
        >
          {
            pages.map((pageObj) => {
              const viewport = pageObj.page.getViewport(scale);
              const popupIds = popupIdsOnPage[pageObj.page.pageIndex];
              const popupsOnPage = {};
              if (popupIds) {
                popupIds.forEach((popupId) => {
                  popupsOnPage[popupId] = popups[popupId];
                });
              }
              const popupGroupIds = popupGroupsOnPage[pageObj.page.pageIndex];
              const popupGroupsOnCurrentPage = {};
              if (popupGroupIds) {
                popupGroupIds.forEach((popupGroupId) => {
                  popupGroupsOnCurrentPage[popupGroupId] = popupGroups[popupGroupId];
                });
              }
              return (
                <SelfServeBuilderPDFPageCanvas
                  getSuperScriptValue={this.getSuperScriptValue}
                  height={viewport.height}
                  key={pageObj.page.pageIndex}
                  page={pageObj.page}
                  popupGroupsOnPage={popupGroupsOnCurrentPage}
                  popupsOnPage={popupsOnPage}
                  scale={scale}
                  width={viewport.width}
                  isPreviewMode={isPreviewMode}
                  zoomFactor={zoomFactor}
                />
              );
            })
          }
        </div>
      </React.Fragment>
    );
  }
}

SelfServeBuilderCanvas.propTypes = {
  editorRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }).isRequired,
  isLoading: PropTypes.bool.isRequired,
  isOverlayVisible: PropTypes.bool.isRequired,
  isPreviewMode: PropTypes.bool,
  isPreviewAutoOpenDisabled: PropTypes.bool,
  isScalingEnabled: PropTypes.bool,
  isTourDisplayed: PropTypes.bool.isRequired,
  pages: PropTypes.arrayOf(PropTypes.shape()),
  popupGroups: CustomPropTypes.hashmapOf(
    PropTypes.shape({
      label: PropTypes.string,
      type: PropTypes.string,
      pageIndex: PropTypes.number,
      fieldIds: PropTypes.arrayOf(PropTypes.string),
    }),
  ).isRequired,
  popupGroupsOnPage: CustomPropTypes.hashmapOf(
    PropTypes.arrayOf(PropTypes.string),
  ).isRequired,
  popupIdsOnPage: CustomPropTypes.hashmapOf(
    PropTypes.arrayOf(PropTypes.string),
  ).isRequired,
  popups: CustomPropTypes.hashmapOf(
    PropTypes.shape({
      label: PropTypes.string,
      type: PropTypes.string,
      id: PropTypes.string,
      pageIdx: PropTypes.number,
    }),
  ).isRequired,
};

SelfServeBuilderCanvas.defaultProps = {
  isPreviewMode: false,
  isPreviewAutoOpenDisabled: false,
  isScalingEnabled: false,
  pages: [],
};

// Opening and closing the menu changes the dimensions of the parent container.
// This needs to trigger a rerender so that the canvas scales to fit.
const mapStateToProps = ({
  StaticBuilderReducer,
  TooltipTourReducer,
}) => ({
  isMenuOpen: StaticBuilderReducer.isMenuOpen,
  isTourDisplayed: TooltipTourReducer.isTourDisplayed,
});

export const SelfServeBuilderCanvasWithRedux = connect(
  mapStateToProps,
)(SelfServeBuilderCanvas);

export default SelfServeBuilderCanvasWithRedux;



// WEBPACK FOOTER //
// ./src-react/SelfServeBuilder/SelfServeBuilderCanvas.jsx